/**
 * Google sign-in via Identity Services, exchanged for a Supabase session with
 * signInWithIdToken — not supabase.auth.signInWithOAuth('google').
 *
 * The OAuth redirect flow hands the whole handshake to GoTrue: it redirects
 * the browser to Google with a redirect_uri of GoTrue's own address, which is
 * what Google's consent screen shows ("Sign in to continue to
 * <project-ref>.supabase.co"). That address changes only through Supabase's
 * paid Custom Domains add-on — a reverse proxy in front of it can't fix this,
 * because GoTrue sets that value itself when it talks to Google, not from
 * whatever hostname the browser first hit.
 *
 * Identity Services never redirects through GoTrue at all. The browser talks
 * to Google directly, using this app's own origin — whatever is in this
 * client's "Authorized JavaScript origins" in Google Cloud Console — and the
 * app name from the OAuth consent screen branding. Google mints an ID token
 * client-side; that token is handed to Supabase, which verifies it against
 * the same Google Client ID already configured for the provider. No new
 * secret, no plan change.
 *
 * This renders Google's OWN button rather than a Beseter-styled one that
 * forwards its click into a hidden Google button. That forwarding trick was
 * the first version of this file — tested and it failed for real: the
 * `[GSI_LOGGER] Failed to open popup window` error Chrome logs when a popup
 * is opened outside the exact call stack of a trusted user gesture. Any
 * `await` or `setTimeout` between the person's actual click and the
 * forwarded one is enough to lose that trust, and loading the script and
 * initializing GIS both need one. Rendering Google's real button means the
 * click Google sees IS the person's click — nothing forwarded, nothing lost.
 * The visual cost is real: it can't be pixel-matched to Beseter's own
 * `.oauth-btn` styling, only sized and themed close to it.
 */

const CLIENT_ID = '318466076821-17g2jeno7ma1s2s93ttiomi6o93chb4b.apps.googleusercontent.com'
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

let scriptPromise = null
let initialized = false
let credentialCallback = null

function loadScript() {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve()
    const el = document.createElement('script')
    el.src = SCRIPT_SRC
    el.async = true
    el.defer = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('Could not reach Google'))
    document.head.appendChild(el)
  })
  return scriptPromise
}

function ensureInitialized() {
  if (initialized) return
  window.google.accounts.id.initialize({
    client_id: CLIENT_ID,
    // One callback for the module, redirected to whichever caller is
    // currently waiting — see waitForCredential. Google's API takes exactly
    // one initialize() call per client_id; it isn't meant to be re-run per
    // sign-in attempt.
    callback: (response) => credentialCallback?.(response.credential),
    // A returning user can pick a different account rather than being
    // silently signed back into whichever one Google remembers — the same
    // reasoning the old flow's `prompt: 'select_account'` had.
    auto_select: false,
  })
  initialized = true
}

/**
 * Loads the script and draws Google's real button into `host`. Call this
 * from a useEffect as soon as the sign-in screen mounts — the button has to
 * exist and be genuinely clickable before the person reaches for it; drawing
 * it lazily on click is exactly the deferred-click pattern that broke.
 *
 * `isNight` picks GIS's own light/dark theme so the button doesn't glare
 * against Beseter's dark surface — the closest this widget gets to matching
 * the app rather than sitting apart from it.
 */
export async function renderGoogleButton(host, { isNight } = {}) {
  await loadScript()
  ensureInitialized()
  host.innerHTML = ''
  // Every other provider button is `.btn`, width:100% of its column — GIS
  // only accepts a fixed px width, not a percentage, so read the real
  // container width instead of guessing a constant that drifts from
  // whatever the column actually measures on a given screen.
  const width = Math.min(400, Math.round(host.parentElement?.getBoundingClientRect().width || 320))
  window.google.accounts.id.renderButton(host, {
    type: 'standard',
    theme: isNight ? 'filled_black' : 'outline',
    size: 'large',
    shape: 'pill',
    text: 'continue_with',
    logo_alignment: 'left',
    width,
  })
}

/** Resolves with the ID token the next time Google's button completes a
 *  sign-in. There's no reliable "the person closed the popup" signal from
 *  this API — only success calls back — so a caller waiting past a
 *  reasonable window has been abandoned, which the timeout below covers. */
export function waitForCredential() {
  return new Promise((resolve, reject) => {
    credentialCallback = (token) => { credentialCallback = null; resolve(token) }
    setTimeout(() => {
      if (credentialCallback) { credentialCallback = null; reject(new Error('Google sign-in was cancelled or timed out.')) }
    }, 120000)
  })
}
